/**
 * Glue between the "Anpassa text" screen and the framework's headless
 * adaptation session (`createAdaptationSession`, ADR-0102 addendum). Pure —
 * no React, no network: the session is rebuilt client side from what
 * `finetune_get` returns, its action builders turn gestures into log
 * entries, and these helpers handle the bits only a UI needs: own fields
 * created while typing ("Nytt eget fält", appended in the same batch as the
 * text that uses them), chip labels, the field picker and Swedish copy.
 */

import {
  CUSTOM_FIELDS_GROUP_TITLE,
  TOKEN_PATTERN,
  appendAction,
  createAdaptationSession,
  createCompileContext,
  customFieldKey,
  foldCustomFields,
} from '@hyranai/regta-document-ir';
import type {
  ActionLog,
  ActionResult,
  AdaptationSession,
  AvailableField,
  BlockRegistry,
  CompileContextInput,
  CustomFieldType,
  NewActionLogEntry,
} from '@hyranai/regta-document-ir';
import { getComposition } from '@hyranai/regta-blocks';
import type { FinetuneAction } from '../../../api.js';

/* ------------------------------------------------------------------ */
/* Session                                                             */
/* ------------------------------------------------------------------ */

/** What `finetune_get` returns for a live contract (`session`). */
export type SessionInput = {
  templateKey: string;
  templateVersion: string;
  values: Record<string, unknown>;
  context: CompileContextInput;
};

/**
 * Builds the adaptation session over `log`, or `null` when the template has
 * no composition in this bundle. Throws what the compiler throws.
 */
export function buildSession(
  input: SessionInput,
  log: ActionLog,
  registry: BlockRegistry,
  author = 'local',
  mode: 'adapt' | 'suggest' = 'adapt',
): AdaptationSession | null {
  const composition = getComposition(input.templateKey, input.templateVersion, input.values);
  if (!composition) return null;
  return createAdaptationSession({
    composition,
    registry,
    ctx: createCompileContext(input.context),
    values: input.values,
    log,
    author,
    ...(mode === 'suggest' ? { mode } : {}),
  });
}

/** Built entries as `finetune_append` actions (the server stamps author / time). */
export function toFinetuneActions(entries: readonly NewActionLogEntry[]): FinetuneAction[] {
  return entries.map((e) => {
    const { author: _a, at: _t, ...rest } = e as NewActionLogEntry & { author?: string; at?: string };
    return rest as FinetuneAction;
  });
}

/* ------------------------------------------------------------------ */
/* Own fields created while typing                                     */
/* ------------------------------------------------------------------ */

/** An own field created in an open editor; defined when the text is saved. */
export type PendingField = { key: string; label: string; type: CustomFieldType };

export const CUSTOM_FIELD_TYPE_LABEL: Record<CustomFieldType, string> = {
  text: 'Text',
  date: 'Datum',
  number: 'Tal',
  money: 'Belopp',
  boolean: 'Ja/nej',
};

export const CUSTOM_FIELD_TYPES: readonly CustomFieldType[] = ['text', 'date', 'number', 'money', 'boolean'];

/**
 * A fresh `custom_…` key for a new field: never one the log ever claimed, a
 * form slot, a stored value or another pending field.
 */
export function pendingFieldKey(
  session: Pick<AdaptationSession, 'log' | 'form'>,
  values: Readonly<Record<string, unknown>>,
  label: string,
  pending: readonly PendingField[],
): string {
  const taken = new Set<string>([
    ...foldCustomFields(session.log).claimedKeys,
    ...session.form.fields.map((f) => f.key),
    ...Object.keys(values),
    ...pending.map((p) => p.key),
  ]);
  return customFieldKey(label, taken);
}

/** Field keys referenced as `{{key}}` in token texts, without repeats. */
export function tokenKeys(...texts: readonly string[]): string[] {
  const out: string[] = [];
  for (const text of texts) {
    for (const m of text.matchAll(TOKEN_PATTERN)) {
      const key = m[1] as string;
      if (!out.includes(key)) out.push(key);
    }
  }
  return out;
}

/** The pending fields the texts still reference (a chip deleted again is never defined). */
export const usedPending = (pending: readonly PendingField[], ...texts: readonly string[]): PendingField[] => {
  const keys = new Set(tokenKeys(...texts));
  return pending.filter((p) => keys.has(p.key));
};

/**
 * Runs an action builder with own fields defined first: the `defineField`
 * entries are built on `session`, the content entry on a session over the
 * log with those definitions (`rebuild`), and both come back as one batch —
 * so the server validates the definitions before the text that uses them,
 * and cancelling the editor defines nothing.
 */
export function buildWithPending(
  session: AdaptationSession,
  pending: readonly PendingField[],
  rebuild: (log: ActionLog) => AdaptationSession | null,
  build: (s: AdaptationSession) => ActionResult,
): ActionResult {
  if (pending.length === 0) return build(session);
  const defines: NewActionLogEntry[] = [];
  let log = session.log;
  for (const p of pending) {
    const r = session.actions.defineField({ label: p.label, type: p.type, key: p.key });
    if (!r.ok) return r;
    for (const entry of r.entries) {
      defines.push(entry);
      log = appendAction(log, entry).log;
    }
  }
  const next = rebuild(log);
  if (!next) return { ok: false, reason: 'not_allowed' };
  const r = build(next);
  return r.ok ? { ...r, entries: [...defines, ...r.entries] } : r;
}

/* ------------------------------------------------------------------ */
/* Chips + picker                                                      */
/* ------------------------------------------------------------------ */

/** A field the picker offers: the session's, plus pending ones. */
export type PickerField = Pick<AvailableField, 'key' | 'label' | 'group' | 'preview' | 'source'> & { pending?: boolean };

export function pickerFields(available: readonly AvailableField[], pending: readonly PendingField[]): PickerField[] {
  return [
    ...available.map((f) => ({ key: f.key, label: f.label, group: f.group, preview: f.preview, source: f.source })),
    ...pending
      .filter((p) => !available.some((f) => f.key === p.key))
      .map((p) => ({ key: p.key, label: p.label, group: CUSTOM_FIELDS_GROUP_TITLE, preview: null, source: 'custom' as const, pending: true })),
  ];
}

const lowerFirst = (s: string): string => (s ? s.charAt(0).toLocaleLowerCase('sv-SE') + s.slice(1) : s);

/**
 * What a chip shows: the field's label, prefixed with its group when another
 * field has the same label ("Hyresvärd – namn" / "Hyresgäst – namn").
 */
export function chipLabels(fields: readonly PickerField[]): Map<string, string> {
  const count = new Map<string, number>();
  for (const f of fields) count.set(f.label, (count.get(f.label) ?? 0) + 1);
  return new Map(
    fields.map((f) => [f.key, (count.get(f.label) ?? 0) > 1 && f.group ? `${f.group} – ${lowerFirst(f.label)}` : f.label]),
  );
}

export type FieldGroup = { group: string; fields: PickerField[] };

/** The picker's list: grouped like the form (own fields last), filtered by a search on label and group. */
export function groupPickerFields(fields: readonly PickerField[], query = ''): FieldGroup[] {
  const q = query.trim().toLocaleLowerCase('sv-SE');
  const groups: FieldGroup[] = [];
  const ordered = [...fields.filter((f) => f.source !== 'custom'), ...fields.filter((f) => f.source === 'custom')];
  for (const f of ordered) {
    if (q && !f.label.toLocaleLowerCase('sv-SE').includes(q) && !f.group.toLocaleLowerCase('sv-SE').includes(q)) continue;
    const name = f.group || 'Övrigt';
    const g = groups.find((x) => x.group === name);
    if (g) g.fields.push(f);
    else groups.push({ group: name, fields: [f] });
  }
  return groups;
}

export type TokenSegment = { kind: 'text'; text: string } | { kind: 'chip'; key: string };

/**
 * Token text as editor segments. Every `{{key}}` becomes a chip when
 * `isField(key)`; anything else stays text (an unknown token is kept as
 * typed — the framework leaves it literal too).
 */
export function tokenSegments(text: string, isField: (key: string) => boolean): TokenSegment[] {
  const out: TokenSegment[] = [];
  let cursor = 0;
  let literal = '';
  for (const m of text.matchAll(TOKEN_PATTERN)) {
    const at = m.index ?? 0;
    const key = m[1] as string;
    literal += text.slice(cursor, at);
    cursor = at + m[0].length;
    if (!isField(key)) {
      literal += m[0];
      continue;
    }
    if (literal) out.push({ kind: 'text', text: literal });
    literal = '';
    out.push({ kind: 'chip', key });
  }
  literal += text.slice(cursor);
  if (literal) out.push({ kind: 'text', text: literal });
  return out;
}

/** References of `before` that `after` no longer contains (by count): the spots that stop following the form. */
export function droppedReferences(before: string, after: string): string[] {
  const left = new Map<string, number>();
  for (const m of after.matchAll(TOKEN_PATTERN)) left.set(m[1]!, (left.get(m[1]!) ?? 0) + 1);
  const out: string[] = [];
  for (const m of before.matchAll(TOKEN_PATTERN)) {
    const k = m[1]!;
    const n = left.get(k) ?? 0;
    if (n > 0) left.set(k, n - 1);
    else if (!out.includes(k)) out.push(k);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Copy                                                                */
/* ------------------------------------------------------------------ */

const REFUSAL: Record<string, string> = {
  unknown_address: 'Stället finns inte längre i dokumentet. Vyn laddas om.',
  locked: 'Texten är lagstadgad och kan inte ändras eller tas bort.',
  not_allowed: 'Det går inte att göra så på den här delen av dokumentet.',
  unchanged: 'Texten är oförändrad.',
  empty: 'Texten får inte vara tom.',
  unknown_group: 'Ändringen finns inte längre.',
  unknown_entry: 'Ändringen finns inte längre.',
  keep_as_text_unresolvable:
    'Stället ändringen gällde finns inte längre, så den kan inte behållas som text. Ta bort ändringen i stället.',
  superseded_entry: 'Den versionen har ersatts av en senare ändring. Ångra den senaste versionen i stället.',
  stale_redo: 'Det går bara att göra om den senaste ångringen.',
  already_undone: 'Ändringen är redan ångrad.',
  unknown_target: 'Ändringen finns inte längre.',
  invalid_field: 'Fältet behöver ett namn (högst 200 tecken) och en typ.',
  duplicate_field: 'Det finns redan ett fält med det namnet. Välj ett annat namn.',
  unknown_field: 'Fältet finns inte längre. Ladda om sidan och försök igen.',
  field_in_use: 'Fältet används i texten. Ta bort eller ändra texten som använder fältet först.',
  suggestions_only: 'Under granskning blir ändringar förslag. Det här går inte att föreslå.',
  review_suggestions_only: 'Under granskning går det bara att föreslå textändringar och godta eller avvisa förslag.',
  suggestions_require_review: 'Nya förslag kan bara göras medan avtalet granskas.',
  not_a_suggestion: 'Ändringen är inget förslag.',
  already_decided: 'Förslaget är redan godtaget eller avvisat.',
  stale: 'Förslaget är inaktuellt eftersom texten har ändrats. Det kan bara avvisas.',
  depends_on_open_suggestion: 'Förslaget ligger i ett annat förslag. Besluta om det först.',
  open_suggestion: 'Ett öppet förslag kan inte ångras. Avvisa det i stället.',
  suggestion_decision: 'Ett beslut om ett förslag kan inte ångras.',
};

/** Swedish text for a builder refusal or a server `refusal`. */
export function refusalText(reason: string, usedBy?: readonly number[]): string {
  const base = REFUSAL[reason] ?? 'Ändringen kunde inte göras.';
  return reason === 'field_in_use' && usedBy?.length
    ? `${base} (${usedBy.length === 1 ? '1 ändring' : `${usedBy.length} ändringar`}: ${usedBy.map((s) => `#${s}`).join(', ')})`
    : base;
}

/** "Används i 2 ändringar" / "Används inte i texten". */
export const usageLabel = (usedBy: readonly number[]): string =>
  usedBy.length === 0 ? 'Används inte i texten' : usedBy.length === 1 ? 'Används i 1 ändring' : `Används i ${usedBy.length} ändringar`;

